"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useParams } from "next/navigation";
import { Button, Stack, Text } from "@leabankab/component-library";
import { useToast } from "@/components/feedback";
import { Heading, SuccessHero } from "@/components/ui";
import { WalletCreditCard } from "@/components/ui/wallet-credit-card";
import { useRuntimeConfig } from "@/config/runtime-config-context";
import { isTestFlow } from "@/core/flow/test-flow-id";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";
import {
	decryptCardPayload,
	exportPublicKey,
	generateKeyPair,
} from "@/features/add-to-wallet/utils/crypto";
import { useAcknowledgeTask } from "@/hooks";
import { ProcessType, TaskType } from "@/types";
import { MOCK_WALLET_CARD_DETAILS } from "./mock-card-details";
import { AddToWalletContext } from "./types";
import { CardDetails, ElektraClient } from "./types/add-to-wallet";

interface Props {
	context: AddToWalletContext;
	elektra: ElektraClient;
	taskId: string;
	customerIdentifier: string | undefined;
}

type CardDetailsStage = "token" | "card" | "decrypt";

const CARD_WIDTH = 300;

const CheckAnimation = dynamic(
	() =>
		import("@leabankab/component-library").then(
			({ CheckAnimation }) => CheckAnimation
		),
	{ ssr: false }
);

const AddToWalletPage = ({
	context,
	elektra,
	taskId,
	customerIdentifier,
}: Props) => {
	const t = useTranslations("pages.AddToWalletPage");
	const { flowId } = useParams<{ flowId: string }>();
	const { showToast } = useToast();
	const [loading, setLoading] = useState(false);
	const [hidden, setHidden] = useState(true);
	const [cardDetails, setCardDetails] = useState<CardDetails | undefined>();

	const cardHeight = CARD_WIDTH / 1.586;
	const { mock, testFlow } = useRuntimeConfig();
	const isMock = mock || isTestFlow(flowId, testFlow);
	const successParagraphs = t("longTextSuccess")
		.split(/\n\n+/)
		.map((paragraph) => paragraph.trim())
		.filter(Boolean);

	useAcknowledgeTask({
		taskId,
		taskType: TaskType.AddToWallet,
		processType: ProcessType.CardSE,
	});

	const revealCardDetails = () => {
		if (isMock) {
			setCardDetails(MOCK_WALLET_CARD_DETAILS);
			setHidden(false);
			return;
		}

		void (async () => {
			let stage: CardDetailsStage = "token";
			let statusCode: number | undefined;
			try {
				setLoading(true);

				const tokenRes = await fetch("/api/elektra/token", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(elektra),
				});
				if (!tokenRes.ok) {
					statusCode = tokenRes.status;
					throw new Error("Failed to obtain token");
				}

				const { bearerToken } = await tokenRes.json();
				stage = "card";
				const { publicKey, privateKey } = await generateKeyPair();
				const exportedKey = await exportPublicKey(publicKey);

				const res = await fetch("/api/elektra", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						bearerToken,
						creditCardId: context.cardId,
						publicKey: exportedKey,
						customerIdentifier,
					}),
				});

				if (!res.ok) {
					statusCode = res.status;
					throw new Error("Failed to obtain card");
				}
				const encrypted = await res.json();

				stage = "decrypt";
				const decryptedText = await decryptCardPayload(encrypted, privateKey);

				setCardDetails(decryptedText);
				setHidden(false);
			} catch (error) {
				void sendClientLog({
					level: "error",
					event: LogEvent.ElektraCardDetailsFailed,
					outcome: "failure",
					message: "Could not reveal card details from Elektra",
					task: TaskType.AddToWallet,
					flowId,
					statusCode,
					error,
					context: { stage },
				});
				showToast({ severity: "error", message: t("cardDetailsFailed") });
			} finally {
				setLoading(false);
			}
		})();
	};

	return (
		<Stack className="gap-2.5 w-full">
			<SuccessHero icon={<CheckAnimation color="tertiary" />} spacing={0.5}>
				<Heading level={1} textStyle="heading-lg">
					{t("header")}
				</Heading>
				<Stack className="gap-1 items-center">
					{successParagraphs.map((paragraph, index) => (
						<Text key={index} textStyle="body-lg">
							{paragraph}
						</Text>
					))}
				</Stack>
			</SuccessHero>

			<Stack className="items-center w-full gap-1.5">
				<WalletCreditCard
					cardNumber={cardDetails?.cardNumber}
					cvv={cardDetails?.cvv}
					expirationDate={cardDetails?.expirationDate}
					width={CARD_WIDTH}
					height={cardHeight}
					hidden={hidden}
					loading={loading}
				/>

				<Button
					className="w-full"
					onClick={() =>
						cardDetails ? setHidden(!hidden) : revealCardDetails()
					}
					disabled={loading}
				>
					{hidden ? t("showDetails") : t("hideDetails")}
				</Button>
			</Stack>
		</Stack>
	);
};

export default AddToWalletPage;
